import {
    getAdminCompanies,
    getAdminCompaniesIdCreditsLedger,
    patchAdminCompaniesIdArchive,
    patchAdminCompaniesIdBilling,
    patchAdminCompaniesIdFeatureFlags,
    postAdminCompaniesIdCredits,
    type AdminCompanyBillingBodySchema,
    type AdminCompanyCreditLedgerEntrySchema,
    type AdminCompanyCreditLedgerPayloadSchema,
    type ArchiveCompanyBodySchema,
    type FeatureFlagsBodySchema,
} from '@/generated/api-client'
import axios from 'axios'
import { FeatureFlags } from '@/constants/enums/feature-flags.enum'
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client'
import type { AdminFeatureFlagEntry } from '@/utils/feature-flags.util'

configureOpenApiClient()

export type OrgFeatureFlagsShape = Record<FeatureFlags, AdminFeatureFlagEntry>

export type FeatureFlagPatchBody = FeatureFlagsBodySchema

export type ArchiveCompanyBody = ArchiveCompanyBodySchema

export type CompanyBillingPatchBody = AdminCompanyBillingBodySchema & {
    /** Opt-in: sync Stripe pause_collection from currentPeriodEnd. Default false on the API. */
    syncStripePause?: boolean
    /** Opt-in: cancel or update Stripe subscription to match assigned plan. Default false. */
    syncStripeSubscription?: boolean
}

export type ArchiveCompanyResult = {
    id: number
    archived: boolean
    archivedAt: string | null
}

export type AdminCompanyDepartment = {
    id: number
    departmentName: string
    createdAt: string
    updatedAt: string
}

export type AdminCompanyBillingPlan = {
    id: number
    code: string
    name: string
    isFreemium: boolean
}

export type AdminCompanyBillingSubscription = {
    status: string
    plan: AdminCompanyBillingPlan | null
}

export type AdminCompanyListItem = {
    id: number
    companyName: string | null
    companySlug: string
    externalAccountId: string | null
    createdAt: string
    updatedAt: string
    archived: boolean
    archivedAt: string | null
    config: {
        id: number
        autoApprove: boolean
        autoApproveOffers: boolean
        offerTemplateDocument: string | null
        defaultApprovers: { email: string }[]
        showCompanyLogo?: boolean
        companyLogoUrl?: string | null
    } | null
    Department: AdminCompanyDepartment[]
    billingSubscription: AdminCompanyBillingSubscription | null
}

export type AdminCompanyGridRow = AdminCompanyListItem & {
    logoUrl?: string
    planName: string
}

function toIsoString(value: unknown): string {
    if (typeof value === 'string') return value
    if (value instanceof Date) return value.toISOString()
    if (value && typeof value === 'object' && 'toISOString' in value && typeof (value as { toISOString: unknown }).toISOString === 'function') {
        return String((value as { toISOString: () => string }).toISOString())
    }
    return String(value ?? '')
}

function normalizeAdminCompanyConfig(raw: Record<string, unknown> | null | undefined): AdminCompanyListItem['config'] {
    if (!raw) return null

    return {
        id: Number(raw.id),
        autoApprove: Boolean(raw.autoApprove),
        autoApproveOffers: Boolean(raw.autoApproveOffers),
        offerTemplateDocument: (raw.offerTemplateDocument as string | null) ?? null,
        defaultApprovers: Array.isArray(raw.defaultApprovers)
            ? raw.defaultApprovers.map((approver) => {
                  const entry = approver as Record<string, unknown>
                  return { email: String(entry.email ?? '') }
              })
            : [],
        showCompanyLogo: Boolean(raw.showCompanyLogo),
        companyLogoUrl: (raw.companyLogoUrl as string | null) ?? null,
    }
}

function normalizeAdminCompanyBillingSubscription(raw: Record<string, unknown> | null | undefined): AdminCompanyListItem['billingSubscription'] {
    if (!raw) return null
    const planRaw = raw.plan as Record<string, unknown> | null | undefined
    return {
        status: String(raw.status ?? ''),
        plan: planRaw
            ? {
                  id: Number(planRaw.id),
                  code: String(planRaw.code ?? ''),
                  name: String(planRaw.name ?? ''),
                  isFreemium: Boolean(planRaw.isFreemium),
              }
            : null,
    }
}

function normalizeAdminCompanyListItem(raw: Record<string, unknown>): AdminCompanyListItem {
    const config = normalizeAdminCompanyConfig(raw.config as Record<string, unknown> | null | undefined)
    const departments = Array.isArray(raw.Department) ? raw.Department : []

    return {
        id: Number(raw.id),
        companyName: (raw.companyName as string | null) ?? null,
        companySlug: String(raw.companySlug ?? ''),
        externalAccountId: (raw.externalAccountId as string | null) ?? null,
        createdAt: toIsoString(raw.createdAt),
        updatedAt: toIsoString(raw.updatedAt),
        archived: Boolean(raw.archived),
        archivedAt: raw.archivedAt != null ? toIsoString(raw.archivedAt) : null,
        config,
        Department: departments.map((dept) => {
            const d = dept as Record<string, unknown>
            return {
                id: Number(d.id),
                departmentName: String(d.departmentName ?? ''),
                createdAt: toIsoString(d.createdAt),
                updatedAt: toIsoString(d.updatedAt),
            }
        }),
        billingSubscription: normalizeAdminCompanyBillingSubscription(raw.billingSubscription as Record<string, unknown> | null | undefined),
    }
}

export async function fetchAdminCompaniesList(): Promise<AdminCompanyListItem[]> {
    const { data } = await getAdminCompanies({ throwOnError: true })

    const envelope = data as { data?: unknown } | null | undefined
    const payload = envelope?.data
    const items = Array.isArray(payload) ? payload : payload ? [payload] : []

    return items.map((item) => normalizeAdminCompanyListItem(item as Record<string, unknown>))
}

export async function archiveAdminCompany(companyId: number, body: ArchiveCompanyBody): Promise<ArchiveCompanyResult> {
    const { data } = await patchAdminCompaniesIdArchive({
        path: { id: companyId.toString() },
        body,
        throwOnError: true,
    })

    const envelope = data as { data?: { id?: unknown; archived?: unknown; archivedAt?: unknown } } | null | undefined
    if (!envelope?.data) {
        throw new Error('Invalid archive response')
    }

    return {
        id: Number(envelope.data.id),
        archived: Boolean(envelope.data.archived),
        archivedAt: envelope.data.archivedAt != null ? toIsoString(envelope.data.archivedAt) : null,
    }
}

export async function patchCompanyFeatureFlags(companyId: number, body: FeatureFlagPatchBody): Promise<OrgFeatureFlagsShape> {
    const { data } = await patchAdminCompaniesIdFeatureFlags({
        path: { id: companyId.toString() },
        body,
        throwOnError: true,
    })

    const envelope = data as { data?: { featureFlags?: unknown } } | null | undefined
    if (!envelope?.data?.featureFlags) {
        throw new Error('Invalid feature flags response')
    }
    return envelope.data.featureFlags as OrgFeatureFlagsShape
}

export async function patchCompanyBilling(companyId: number, body: CompanyBillingPatchBody) {
    const { data } = await patchAdminCompaniesIdBilling({
        path: { id: companyId.toString() },
        body: body as AdminCompanyBillingBodySchema,
        throwOnError: true,
    })

    if (!data?.data) {
        throw new Error('Invalid company billing response')
    }
    return data.data
}

export type CompanyCreditsBalance = {
    includedCredits: number
    packCredits: number
    availableCredits: number
    unlimited: boolean
}

export type CompanyCreditsGrantBody = {
    credits: number
    bucket?: 'included' | 'pack'
    idempotencyKey?: string
}

export type CompanyCreditsGrantResult = {
    applied: boolean
    credits: number
    bucket: 'included' | 'pack'
    balance: CompanyCreditsBalance
}

export async function fetchCompanyCredits(companyId: number): Promise<CompanyCreditsBalance> {
    const { data } = await axios.get<{ data?: CompanyCreditsBalance; message?: string }>(`/api/admin/companies/${companyId}/credits`)
    if (!data?.data) {
        throw new Error(data?.message ?? 'Invalid credit balance response')
    }
    return data.data
}

export type CompanyCreditLedgerRow = AdminCompanyCreditLedgerEntrySchema
export type CompanyCreditLedgerPage = AdminCompanyCreditLedgerPayloadSchema

export async function fetchCompanyCreditLedger(companyId: number, params: { page?: number; pageSize?: number } = {}): Promise<CompanyCreditLedgerPage> {
    const { data } = await getAdminCompaniesIdCreditsLedger({
        path: { id: companyId.toString() },
        query: { page: params.page, pageSize: params.pageSize },
        throwOnError: true,
    })
    if (!data?.data) {
        throw new Error('Invalid credit ledger response')
    }
    return data.data
}

export async function grantCompanyCredits(companyId: number, body: CompanyCreditsGrantBody): Promise<CompanyCreditsGrantResult> {
    const { data } = await postAdminCompaniesIdCredits({
        path: { id: companyId.toString() },
        body,
        throwOnError: true,
    })
    if (!data?.data) {
        throw new Error('Invalid grant credits response')
    }
    return data.data as CompanyCreditsGrantResult
}
